import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { PermissionGate } from "@/components/common/PermissionGate";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
} from "@/components/ui/alert-dialog";
import { Edit, MoreVertical, Trash2, DollarSign } from "lucide-react";
import { formatDate } from "@/lib/utils";
import type {
  PricingComponentListDto,
  ComponentType,
} from "@/types/pricing-component";
import { useDeletePricingComponent } from "@/hooks/usePricingComponents";

// ============================================================================
// Types
// ============================================================================

interface PricingComponentTableProps {
  components: PricingComponentListDto[];
  isLoading?: boolean;
  onEdit?: (component: PricingComponentListDto) => void;
  onViewPrices?: (component: PricingComponentListDto) => void;
}

// ============================================================================
// Helper Functions
// ============================================================================

function getComponentTypeBadgeVariant(
  type: ComponentType,
): "default" | "secondary" | "outline" {
  switch (type) {
    case "Resource":
      return "default";
    case "Addon":
      return "secondary";
    case "Service":
      return "outline";
    case "Usage":
      return "outline";
    default:
      return "outline";
  }
}

// ============================================================================
// Component
// ============================================================================

export function PricingComponentTable({
  components,
  isLoading = false,
  onEdit,
  onViewPrices,
}: PricingComponentTableProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedComponent, setSelectedComponent] =
    useState<PricingComponentListDto | null>(null);
  const deleteMutation = useDeletePricingComponent();

  const handleDeleteClick = (component: PricingComponentListDto) => {
    setSelectedComponent(component);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedComponent) {
      await deleteMutation.mutateAsync(selectedComponent.id);
      setDeleteDialogOpen(false);
      setSelectedComponent(null);
    }
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  // Render empty state
  if (components.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No pricing components found</p>
        <p className="text-sm text-muted-foreground mt-2">
          Create a new pricing component to get started
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-center">Prices</TableHead>
              <TableHead>Created At</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {components.map((component) => (
              <TableRow key={component.id}>
                <TableCell className="font-medium">{component.name}</TableCell>
                <TableCell className="font-mono text-sm text-muted-foreground">
                  {component.slug}
                </TableCell>
                <TableCell>{component.unit}</TableCell>
                <TableCell>
                  <Badge
                    variant={getComponentTypeBadgeVariant(
                      component.componentType,
                    )}
                  >
                    {component.componentType}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge
                    variant={component.isActive ? "default" : "destructive"}
                  >
                    {component.isActive ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant="outline">{component.priceCount || 0}</Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatDate(component.createdAt)}
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <PermissionGate roles={["SuperAdmin", "BillingAdmin"]}>
                        {onEdit && (
                          <DropdownMenuItem onClick={() => onEdit(component)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                        )}
                      </PermissionGate>
                      {onViewPrices && (
                        <DropdownMenuItem
                          onClick={() => onViewPrices(component)}
                        >
                          <DollarSign className="h-4 w-4 mr-2" />
                          View Prices
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <PermissionGate roles={["SuperAdmin", "BillingAdmin"]}>
                        <DropdownMenuItem
                          onClick={() => handleDeleteClick(component)}
                          className="text-destructive-ink"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </PermissionGate>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Pricing Component</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              Are you sure you want to delete the pricing component &quot;
              {selectedComponent?.name}&quot;? This action cannot be undone and
              will remove all associated prices.
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
